import type { ISbStoryData } from '@storyblok/js';

import type { Locale } from '~i18n/i18nConfig';
import type {
    ContentType,
    StoryblokBorderRadiusDataSource,
    StoryblokSizingDataSource,
    StoryblokSpacingDataSource,
    StoryblokUiColorsDataSource,
    StoryblokAlignmentDataSource,
    StoryblokAspectRatioDataSource,
    StoryblokFontSizesDataSource,
    StoryblokDurationDataSource,
    StoryblokAccordion,
    StoryblokAccordionItem,
    StoryblokBlogListing,
    StoryblokBlogPost,
    StoryblokContent,
    StoryblokLegalPage,
    StoryblokNumber,
    StoryblokLabel,
    StoryblokPage,
    StoryblokSection,
    StoryblokFeatureFleequid,
    StoryblokFleequidStats,
    StoryblokHero,
    StoryblokNavMenuItem,
    StoryblokNavMenuChildItem,
    StoryblokGlobalConfig,
    StoryblokSaleListingCarousel,
    StoryblokSearchForm,
    StoryblokSellForm,
    StoryblokUiAlert,
    StoryblokUiBadge,
    StoryblokUiButton,
    StoryblokUiCarousel,
    StoryblokUiPageCard,
    StoryblokUiPageColumns,
    StoryblokUiPageCta,
    StoryblokUiPageFeature,
    StoryblokUiPageGrid,
    StoryblokUiPageList,
    StoryblokUiPageLogos,
    StoryblokUiPageLogosItem,
    StoryblokUiPageHero,
    StoryblokUiPageSection,
    StoryblokUiSeparator,
    StoryblokImage,
    StoryblokVideo,
    StoryblokWhatsappLink,
} from '~storyblok/types/fleequid/storyblok-components';
import type { StoryblokMultilinkUrl, StoryblokMultilink, StoryblokAsset } from '~storyblok/types/storyblok';

/**
 * Makes all blok content fields optional while keeping
 * structural fields (_uid, component) required.
 * This reflects the live preview reality where required
 * fields may not have values yet.
 */
type StoryblokBlokBase = '_uid' | 'component';

type StoryblokMaybeLivePreview<T> = Pick<T, Extract<keyof T, StoryblokBlokBase>> & Partial<Omit<T, StoryblokBlokBase>>;

export interface StoryblokTranslatedSlug {
    path: string;
    name: string | null;
    lang: Locale;
    published: boolean | null;
}

export type StoryblokFullPath = {
    locale: Locale;
    path: string;
};

export type StoryblokPreviewResponse =
    | {
          success: true;
          story: ISbStoryData<ContentType>;
          links: StoryblokMultilinkUrl[];
      }
    | {
          success: false;
          error?: Record<string, any>;
      };

export type {
    ContentType,
    StoryblokBorderRadiusDataSource,
    StoryblokSizingDataSource,
    StoryblokSpacingDataSource,
    StoryblokUiColorsDataSource,
    StoryblokAlignmentDataSource,
    StoryblokAspectRatioDataSource,
    StoryblokFontSizesDataSource,
    StoryblokDurationDataSource,
    StoryblokMultilink,
    StoryblokMultilinkUrl,
    StoryblokMaybeLivePreview,
    StoryblokAccordion,
    StoryblokAccordionItem,
    StoryblokContent,
    StoryblokBlogListing,
    StoryblokBlogPost,
    StoryblokLegalPage,
    StoryblokPage,
    StoryblokAsset,
    StoryblokImage,
    StoryblokNumber,
    StoryblokLabel,
    StoryblokVideo,
    StoryblokSection,
    StoryblokFeatureFleequid,
    StoryblokFleequidStats,
    StoryblokHero,
    StoryblokNavMenuItem,
    StoryblokNavMenuChildItem,
    StoryblokGlobalConfig,
    StoryblokSaleListingCarousel,
    StoryblokSearchForm,
    StoryblokSellForm,
    StoryblokUiAlert,
    StoryblokUiBadge,
    StoryblokUiButton,
    StoryblokUiCarousel,
    StoryblokUiPageCard,
    StoryblokUiPageColumns,
    StoryblokUiPageCta,
    StoryblokUiPageGrid,
    StoryblokUiPageFeature,
    StoryblokUiPageList,
    StoryblokUiPageLogos,
    StoryblokUiPageLogosItem,
    StoryblokUiPageHero,
    StoryblokUiPageSection,
    StoryblokUiSeparator,
    StoryblokWhatsappLink,
};

export const isStoryblokStoryMultilinkUrl = (data: any): data is StoryblokMultilinkUrl => data !== undefined && !!data.id && !!data.url;
